import { trpc, RouterOutputs } from "@utils/trpc";
import { captureException } from "@utils/captureException";
import { useData, useDataAuth } from "../../runtime/provider";
import { getQueryKey } from "@trpc/react-query";
import type { InfiniteData } from "@tanstack/react-query";
import type { z } from "zod";
import { recommendedArtistsPageWireSchema } from "@libs/wire";
import { infiniteDataSchema } from "@utils/durableCache/schemas";
import { makeOnUserChange } from "@hooks/useOnUserChange";
import { cachedArtists } from "@sqlite/schema";
import { artistSchema } from "@libs/wire";

// Clear + refetch ONLY on a real login/logout transition (see the factory's
// doc comment — the old inline useEffect refired on every screen mount).
const useOnArtistsUserChange = makeOnUserChange();

const RECOMMENDED_ARTISTS_INITIAL_LIMIT = 4;
export const RECOMMENDED_ARTISTS_PAGE_LIMIT = 2;

export const RECOMMENDED_ARTISTS_INPUTS = {
  initialLimit: RECOMMENDED_ARTISTS_INITIAL_LIMIT,
  limit: RECOMMENDED_ARTISTS_PAGE_LIMIT,
};

type InfiniteArtistsQuery = ReturnType<typeof useRecommendedArtists>;
export type RecommendedArtistsData =
  InfiniteArtistsQuery["data"]["pages"][number]["data"][number];

type RecommendedArtistsPage =
  RouterOutputs["catalog"]["artists"]["listRecommendedArtistsPaginated"];

// One-way drift check: see useForYou for rationale.
type _RecommendedArtistsPageWireSchemaCheck =
  RecommendedArtistsPage extends z.infer<
    typeof recommendedArtistsPageWireSchema
  >
    ? true
    : never;
const _recommendedArtistsPageWireSchemaDriftCheck: _RecommendedArtistsPageWireSchemaCheck = true;
void _recommendedArtistsPageWireSchemaDriftCheck;

const recommendedArtistsInfiniteSchema = infiniteDataSchema(
  recommendedArtistsPageWireSchema,
  recommendedArtistsPageWireSchema.shape.endCursor,
);

/**
 * Get the list of recommended artists for the home screen for the current user
 */
export const useRecommendedArtists = (options?: any) => {
  const data = useData();
  const { firebaseUser } = useDataAuth();
  const queryKey = getQueryKey(
    trpc.catalog.artists.listRecommendedArtistsPaginated,
    RECOMMENDED_ARTISTS_INPUTS,
    "infinite",
  );
  const [, infiniteQuery] =
    trpc.catalog.artists.listRecommendedArtistsPaginated.useSuspenseInfiniteQuery(
      RECOMMENDED_ARTISTS_INPUTS,
      {
        getNextPageParam: (lastPage: { endCursor?: number }) =>
          lastPage.endCursor,
        staleTime: 60 * 60 * 1000, // 1 hour — recommendations refresh hourly
        // gcTime must outlive staleTime (see useForYou, 2026-08-03).
        gcTime: 60 * 60 * 1000,
        ...data.cache.durableInfiniteOptions<RecommendedArtistsPage>(
          queryKey,
          recommendedArtistsInfiniteSchema,
          {
            // Pre-warm `cached_artists` from feed pages so subsequent
            // `useArtists([id])` resolves from the per-entity cache.
            decompose: (data: InfiniteData<RecommendedArtistsPage>) =>
              data.pages.flatMap((p) => p.data),
            decomposeTable: cachedArtists,
            decomposeIdField: "id",
            decomposeSchema: artistSchema,
          },
        ),
        ...options,
      },
    );

  // Undefined until Firebase reports, null once it reports signed out.
  useOnArtistsUserChange(firebaseUser && firebaseUser.uid, () => {
    data.utils.catalog.artists.listRecommendedArtistsPaginated.setInfiniteData(
      RECOMMENDED_ARTISTS_INPUTS,
      () => undefined,
    );
    data.utils.catalog.artists.listRecommendedArtistsPaginated
      .refetch()
      .catch((error) => {
        captureException(error, {
          channel: "breadcrumb",
          area: "query",
          operation: "refetch-recommended-artists-after-user-change",
        });
      });
  });

  return infiniteQuery;
};
